import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageCircle, Mail, Copy, RefreshCw, Loader2, ListOrdered, Check } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { id: "email", label: "Email", icon: Mail },
];
const TONES = ["Professional", "Friendly", "Direct", "Consultative", "Premium", "Minimal"];
const ADJUSTMENTS = ["Make shorter", "Make more human"];

export function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text).then(() => true);
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand("copy"); document.body.removeChild(ta);
    return Promise.resolve(true);
  } catch { return Promise.resolve(false); }
}

export function PitchSequencer({ oppId, compact = false }) {
  const [channel, setChannel] = useState("whatsapp");
  const [tone, setTone] = useState(null);
  const [touches, setTouches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "all" | step number
  const [saved, setSaved] = useState(false);

  const load = useCallback(async (ch) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sequence/${oppId}`, { params: { channel: ch } });
      setTouches(data.touches);
      if (data.tone) setTone(data.tone);
    } finally { setLoading(false); }
  }, [oppId]);

  useEffect(() => { load(channel); }, [channel, load]);

  const generate = async ({ adjust = null, touch = null, newTone = tone } = {}) => {
    setBusy(touch ?? "all");
    try {
      const { data } = await api.post("/sequence", { opportunity_id: oppId, channel, adjust, tone: newTone, touch });
      setTouches(data.touches);
      if (data.tone) setTone(data.tone);
    } catch { toast.error("Could not generate sequence."); }
    finally { setBusy(null); }
  };

  const edit = (step, message) => { setTouches((ts) => ts.map((t) => (t.step === step ? { ...t, message } : t))); setSaved(false); };
  const persist = async () => {
    if (!touches) return;
    await api.put(`/sequence/${oppId}`, { channel, touches });
    setSaved(true); setTimeout(() => setSaved(false), 1500);
  };
  const copy = async (text) => ((await copyText(text)) ? toast.success("Copied") : toast.error("Couldn't copy — select and copy manually."));
  const copyAll = () => copy(touches.map((t) => `TOUCH ${t.step} · ${t.timing} · ${t.title}\n\n${t.message}`).join("\n\n— — —\n\n"));

  return (
    <div className="space-y-4" data-testid="pitch-sequencer">
      <div className="flex flex-wrap items-center gap-2">
        {CHANNELS.map((c) => (
          <button key={c.id} onClick={() => setChannel(c.id)} data-testid={`seq-channel-${c.id}`}
            className={`px-3 py-2 rounded-lg text-sm border flex items-center gap-2 transition-all ${channel === c.id ? "bg-primary/15 border-primary/50 text-primary" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"}`}>
            <c.icon className="w-4 h-4" /> {c.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-slate-500 font-mono uppercase">Tone</span>
          <Select value={tone || ""} onValueChange={(v) => { setTone(v); if (touches) generate({ newTone: v }); }}>
            <SelectTrigger data-testid="seq-tone" className="h-8 w-[140px] text-xs bg-slate-900 border-slate-700 text-slate-300"><SelectValue placeholder="Agency default" /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">{TONES.map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : !touches ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-8 text-center">
          <ListOrdered className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-sm text-slate-300 font-medium">3-touch pitch sequence</p>
          <p className="text-xs text-slate-500 mt-1 mb-4">Personalised opener → contextual value follow-up → new angle + respectful close. Built from the Opportunity Brief, evidence and your agency profile. Nothing is sent automatically.</p>
          <Button onClick={() => generate()} disabled={busy === "all"} className="bg-primary hover:bg-emerald-600 text-white" data-testid="generate-sequence-btn">
            {busy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Generate ${channel === "email" ? "email" : "WhatsApp"} sequence`}
          </Button>
        </div>
      ) : (
        <>
          <div className="relative space-y-3">
            {busy === "all" && <div className="absolute inset-0 bg-background/60 grid place-items-center rounded-xl z-10"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>}
            {touches.map((t) => (
              <div key={t.step} className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden" data-testid={`touch-${t.step}`}>
                <div className="px-4 py-2.5 flex items-center justify-between border-b border-slate-800 bg-slate-900/70">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-mono font-bold grid place-items-center">{t.step}</span>
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{t.title}</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t.timing}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => copy(t.message)} className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-slate-800" title="Copy" data-testid={`copy-touch-${t.step}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={() => generate({ touch: t.step })} disabled={busy != null} className="p-1.5 rounded-md text-slate-400 hover:text-primary hover:bg-slate-800 disabled:opacity-40" title="Regenerate this touch" data-testid={`regen-touch-${t.step}`}>
                      {busy === t.step ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
                <Textarea value={t.message} onChange={(e) => edit(t.step, e.target.value)} onBlur={persist} rows={compact ? 5 : channel === "email" ? 8 : 5}
                  className="bg-transparent border-0 rounded-none focus-visible:ring-0 font-mono text-[13px] leading-relaxed text-slate-200" data-testid={`touch-message-${t.step}`} />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {ADJUSTMENTS.map((a) => (
              <button key={a} onClick={() => generate({ adjust: a })} disabled={busy != null} data-testid={`seq-adjust-${a.split(" ")[1]}`}
                className="text-[11px] px-2.5 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-slate-400 hover:text-primary hover:border-primary/30 disabled:opacity-50">{a}</button>
            ))}
            <div className="ml-auto flex gap-2">
              <Button size="sm" onClick={copyAll} className="bg-primary hover:bg-emerald-600 text-white" data-testid="copy-sequence-btn"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all</Button>
              <Button size="sm" variant="outline" onClick={() => generate()} disabled={busy != null} className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="regenerate-sequence-btn"><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate</Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
            {saved && <span className="text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" /> Edits saved ·</span>}
            Edits save automatically. Messages are AI-drafted from verified signals — review before sending. Nothing is sent by LeadForge.
          </p>
        </>
      )}
    </div>
  );
}
